import {
  initializeFaro,
  FetchTransport,
  ConsoleTransport,
  WebVitalsInstrumentation,
  ErrorsInstrumentation,
  ConsoleInstrumentation,
  SessionInstrumentation,
  browserMeta,
  LogLevel as FaroLogLevel,
} from '@grafana/faro-web-sdk'
import type { Faro, MetaApp } from '@grafana/faro-web-sdk'
import { resolveCollectorUrl } from '../../constants'
import type { MontillaLoadConfig } from '../../types'
import type {
  MontillaDestination,
  EventPayload,
  MontillaLogPayload,
  MontillaErrorPayload,
  MontillaContext,
} from '../../types/destination'
import { getSdkMeta } from '../../sdk'
import { getPageMeta } from '../../browser'

export class FaroDestination implements MontillaDestination {
  readonly name = 'faro'
  private faro: Faro | null = null

  async init(config: MontillaLoadConfig, context: MontillaContext): Promise<void> {
    const url = new URL(window.location.href)
    const metas = [getPageMeta(url), browserMeta, getSdkMeta()]
    const transports = this.getTransports(config)

    this.faro = initializeFaro({
      // A CHAVE DO CLIENT viaja aqui, no meta do APP.
      //
      // É o único meta por-app que não passa pela sessão: o `registerInitialMetas`
      // do Faro faz `initial.app = { ...config.app, ...initial.app }`, um espalha
      // sem allowlist, e só o `session` é persistido em `localStorage`. Então este
      // campo chega em cada payload e não pode ser sobrescrito pela visita.
      //
      // No corpo e não num cabeçalho de propósito: cabeçalho depende de o
      // preflight de CORS deixá-lo passar e de nenhum proxy o remover, e as duas
      // falhas são silenciosas — o evento chega e a atribuição some. O corpo
      // ainda viaja na réplica para o Alloy e funciona em qualquer transporte
      // (um `sendBeacon` não tem como pôr cabeçalho).
      //
      // O cast é o preço de usar essa folga: `MetaApp` do Faro não declara o
      // campo, e a implementação o preserva.
      app: {
        name: config.app.name,
        environment: config.app.environment,
        version: config.app.version,
        namespace: config.app.namespace,
        ...(config.clientKey ? { clientKey: config.clientKey } : {}),
      } as MetaApp,
      trackWebVitalsAttribution: config.track.webVitals,
      sessionTracking: {
        enabled: config.track.session,
        persistent: true,
        session: {
          attributes: {
            // First-touch acquisition referrer (from context.session, frozen for
            // the visit) — falls back to the live referrer if unset.
            referrer: String(context.session.referrer ?? document.referrer ?? ''),
            is_bot: String(context.session.is_bot ?? 'false'),
            ...(context.location ?? {}),
            ...this.extractUtmFromContext(context),
            // A CHAVE DO CLIENT NÃO MORA AQUI — ver `getTransports`.
            //
            // Ela ficava neste bag, e não podia: a sessão é `persistent: true`,
            // guardada em `localStorage` por ORIGEM e compartilhada entre os apps
            // de um host de propósito (ela responde "quem é este visitante no
            // domínio"). E ao retomar uma sessão válida o Faro faz os atributos
            // GUARDADOS vencerem os configurados:
            //
            //   attributes: { ...config.session.attributes, ...stored.attributes }
            //
            // Então o primeiro app que o visitante abre escreve a chave dele, e
            // cada evento dos outros apps do mesmo host chega com ela. Medido em
            // produção: rotas de um site creditadas a outro, no mesmo evento em
            // que o `route_pattern` era o do app correto.
            //
            // A chave viaja no meta `app`, que é remontado da config a cada init
            // e não é persistido — ver a nota lá.
          },
        },
      },
      user: {
        hash: context.user.hash,
        ...(context.user.id && { id: context.user.id }),
        ...(context.user.email && { email: context.user.email }),
      },
      instrumentations: [
        ...(config.track.webVitals ? [new WebVitalsInstrumentation()] : []),
        ...(config.track.errors ? [new ErrorsInstrumentation()] : []),
        ...(config.track.logs ? [new ConsoleInstrumentation({ disabledLevels: [] })] : []),
        ...(config.track.session ? [new SessionInstrumentation()] : []),
      ],
      metas,
      ...(config.page?.name && { view: { name: config.page.name } }),
      transports,
    })
  }

  track(event: EventPayload): void {
    if (!this.faro) return
    // View/page meta are written upstream by api/page.ts via faro.api.setView /
    // faro.api.setPage. Faro auto-attaches the live meta on every pushEvent.
    this.faro.api.pushEvent(event.name, event.attributes as Record<string, string> | undefined)
  }

  // identify() is no longer wired up — api/identify.ts writes user meta to
  // Faro via setUser, then dispatches track('identify'). track() above
  // calls pushEvent('identify', attrs) just like any other event.

  /**
   * Devtools preview. Faro is the canonical collector: every dispatched event
   * is pushed verbatim, so it always fires once the instance is live.
   */
  inspect(): { fired: boolean; reason?: string } {
    return this.faro ? { fired: true } : { fired: false, reason: 'Faro não inicializado' }
  }

  log(payload: MontillaLogPayload): void {
    if (!this.faro) return
    const level = FaroLogLevel[payload.level] ?? FaroLogLevel.LOG
    this.faro.api.pushLog([payload.message], {
      context: payload.context,
      level,
    })
  }

  error(payload: MontillaErrorPayload): void {
    if (!this.faro) return
    this.faro.api.pushError(payload.error, { context: payload.context })
  }

  destroy(): void {
    this.faro?.pause()
    this.faro = null
  }

  /**
   * Exposes the internal Faro instance for backward compatibility.
   * Used by state.ts faro getter during migration.
   */
  getFaroInstance(): Faro | null {
    return this.faro
  }

  private getTransports(config: MontillaLoadConfig) {
    // development → ConsoleTransport (no network noise from local dev).
    // production and homolog → real backend, URL resolved by environment.
    const env = config.app.environment ?? 'production'
    if (env === 'development') {
      return [new ConsoleTransport()]
    }

    return [new FetchTransport({ url: resolveCollectorUrl(env, config.collector?.url) })]
  }

  private extractUtmFromContext(context: MontillaContext): Record<string, string> {
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id', 'gclid']
    const result: Record<string, string> = {}
    for (const key of utmKeys) {
      if (context.session[key]) {
        result[key] = context.session[key]
      }
    }
    return result
  }
}
